"use client";

import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { ErrorBoundary } from "@/components/error/error-boundary";
import { DialogDismissalLayer } from "@/components/ui/dialog-dismissal-layer";
import { LoadFailureDialog } from "@/components/ui/load-failure-dialog";
import { useSearchContextSafe } from "@/components/search/search-context";
import { useActiveViewAs } from "@/components/view-as/use-active-view-as";
import { OPEN_SUPPORT_CHAT_EVENT } from "@/lib/chat/open-public-chat";

const PublicChatPanel = lazy(() =>
  import("./public-chat-panel").then((module) => ({
    default: module.PublicChatPanel,
  })),
);

function useLauncherState(hidden: boolean) {
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [openedForMessage, setOpenedForMessage] = useState<string | null>(null);
  const { publicChatMessage, clearPublicChatMessage } = useSearchContextSafe();

  if (!hidden && publicChatMessage && openedForMessage !== publicChatMessage) {
    setOpenedForMessage(publicChatMessage);
    setIsOpen(true);
    setHasOpened(true);
  } else if (!publicChatMessage && openedForMessage !== null) {
    setOpenedForMessage(null);
  }
  if (hidden && isOpen) setIsOpen(false);

  useEffect(() => {
    if (hidden && publicChatMessage) clearPublicChatMessage();
  }, [hidden, publicChatMessage, clearPublicChatMessage]);

  const open = useCallback(() => {
    setIsOpen(true);
    setHasOpened(true);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (hidden) return;
    window.addEventListener(OPEN_SUPPORT_CHAT_EVENT, open);
    return () => window.removeEventListener(OPEN_SUPPORT_CHAT_EVENT, open);
  }, [hidden, open]);

  return { isOpen, hasOpened, open, close };
}

function PublicChatLoading({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  return (
    <DialogDismissalLayer open={isOpen} onOpenChange={onClose}>
      <span role="status" className="sr-only">
        Loading Ask Repackify…
      </span>
    </DialogDismissalLayer>
  );
}

export function PublicChatWidget() {
  const viewingAs = useActiveViewAs();
  const launcher = useLauncherState(Boolean(viewingAs));

  if (viewingAs) return null;

  return (
    <>
      {launcher.hasOpened && (
        <ErrorBoundary
          label="public-chat-panel"
          fallback={
            <LoadFailureDialog
              title="Ask Repackify could not load"
              open={launcher.isOpen}
              onOpenChange={launcher.close}
            />
          }
        >
          <Suspense
            fallback={
              <PublicChatLoading
                isOpen={launcher.isOpen}
                onClose={launcher.close}
              />
            }
          >
            <PublicChatPanel
              isOpen={launcher.isOpen}
              onClose={launcher.close}
            />
          </Suspense>
        </ErrorBoundary>
      )}
    </>
  );
}
